import {Input} from '@mantine/core';
import {Space} from 'src/design/atoms/Space';
import React, {useCallback, useMemo} from 'react';
import {UserStatusEnum} from 'src/api/ProactApiClient';
import {Description} from 'src/design/atoms/Description';
import {HelpText} from 'src/design/atoms/HelpText';
import {PersonSelectBox} from 'src/app/databound-elements/PersonSelectBox';
import {useOfflineSectionAvailable} from 'src/app/forms/offline/useOfflineSectionAvailable';
import {formsPersonnelKey} from 'src/app/forms/offline/snapshotKeys';
import {useConnectivity} from 'src/offline/useConnectivity';
import {usePerson} from 'src/redux/selectors/usePerson';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';

export interface FormPersonSelectorV2Props {
  field: IFormFieldV2;
  value: string | number;
  error?: string;
  onChange: (name: string, value: any, displayValue?: string) => void;
}

const OFFLINE_NOTICE =
  'Offline — the personnel list is not cached on this device for the current ' +
  'project. Reconnect to select a person.';

export const FormPersonSelectorV2 = React.memo<FormPersonSelectorV2Props>(
  ({field, value, error, onChange}: FormPersonSelectorV2Props) => {
    const person = usePerson({value: Number(value)});
    const {isOffline} = useConnectivity();
    const unavailable = useOfflineSectionAvailable(formsPersonnelKey) === false;

    const initialOption = useMemo<ISelectOption | null>(
      () =>
        person.name
          ? {id: Number(value), label: person.name}
          : null,
      [person.name, value]
    );

    const queryOptions = useMemo(() => {
      return {
        active: field.personOptions?.includeInactive
          ? [UserStatusEnum.Active, UserStatusEnum.Inactive]
          : [UserStatusEnum.Active],
      };
    }, [field.personOptions]);

    const handlePersonnelChange = useCallback(
      (value: ISelectOption | null): void => {
        onChange(field.name, value?.id, value?.text ?? value?.label);
      },
      [field.name, onChange]
    );

    return (
      <Input.Wrapper
        data-testid={`form-field-${field.name}`}
        label={field.label}
        withAsterisk={field.required}
        error={error}
        description={field.description}>
        <Space mb="xs" />
        <PersonSelectBox
          onSelectChangeCallback={handlePersonnelChange}
          label={''}
          value={String(value)}
          error={!!error}
          disabled={field.disabled || unavailable}
          queryOptions={queryOptions}
          initialOption={initialOption}
        />
        {unavailable ? <HelpText text={OFFLINE_NOTICE} /> : null}
        {isOffline ? null : <Description text={person.description} />}
      </Input.Wrapper>
    );
  }
);
